import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { AdminError, ERRS, type Row } from "./api";
import { useAdmin } from "./context";
import { when } from "./format";
import { Empty, Loading, LoadError, PageHead } from "./parts";

/** PA6 words (DEC-1164). */
const SAVE_FAILED = "Couldn’t save the rules. Nothing changed.";
/** rules_partly_saved: the only failure where "Nothing changed" is false, so it names the goals now on their new values. */
export function partlySaved(goalKeys: unknown, rows: Row[]): string {
  const names = (Array.isArray(goalKeys) ? goalKeys : []).map((g) => String(rows.find((r) => r.goal_key === g)?.goal || g));
  return names.length
    ? `Couldn’t save all the rules. Now on the new values: ${names.join(", ")}. The other goals did not change. Your edits are kept: save again.`
    : `Couldn’t save all the rules. Some goals may be on the new values. Reload to check, then save again.`;
}

type Edit = { enabled: boolean; day: string; cut: string };

/** Inline range checks (PA6): day 1–60 whole, cut-off 1–10. The API checks the same and names the goal. */
export function ruleErrors(e: Edit): { day?: string; cut?: string } {
  const out: { day?: string; cut?: string } = {};
  const d = Number(e.day);
  if (e.day.trim() === "" || !Number.isInteger(d) || d < 1 || d > 60) out.day = ERRS.bad_referral_day;
  const c = Number(e.cut);
  if (e.cut.trim() === "" || !Number.isFinite(c) || c < 1 || c > 10) out.cut = ERRS.bad_cut_off;
  return out;
}

const fromRow = (r: Row): Edit => ({ enabled: !!r.enabled, day: String(r.referral_day ?? ""), cut: String(r.cut_off ?? "") });

/**
 * The form after the rules (re)load. Untouched, it shows the server's values; once edited, a background refetch
 * (window focus) keeps every local edit and only fills in a goal the form has not seen.
 */
export function syncEdits(prev: Record<string, Edit>, rows: Row[], dirty: boolean): Record<string, Edit> {
  const next: Record<string, Edit> = {};
  for (const r of rows) next[r.goal_key] = dirty && prev[r.goal_key] ? prev[r.goal_key] : fromRow(r);
  return next;
}

/** Referral rules (v3.1 PA6): per goal, on/off, the day the average is checked and the cut-off, saved together with a reason. */
export function ReferralRulesTab() {
  const { key, api, me } = useAdmin();
  const client = useQueryClient();
  const q = useQuery({ queryKey: [key, "referral-rules"], queryFn: () => api.get<Row[]>("referral-rules") });
  const writes = !!me?.writes;
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  /** True once a field is edited, until a save lands: a refetch then never replaces the form under the person. */
  const dirty = useRef(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [badGoal, setBadGoal] = useState<{ goal: string; field: "day" | "cut" | null; text: string } | null>(null);

  useEffect(() => {
    setEdits((prev) => syncEdits(prev, q.data || [], dirty.current));
  }, [q.data]);

  const rows: Row[] = q.data || [];
  const errs: Record<string, { day?: string; cut?: string }> = Object.fromEntries(
    rows.map((r) => [r.goal_key, edits[r.goal_key] ? ruleErrors(edits[r.goal_key]) : {}]),
  );
  const anyErr = Object.values(errs).some((e) => e.day || e.cut);
  const set = (g: string, patch: Partial<Edit>) => { setBadGoal(null); dirty.current = true; setEdits((m) => ({ ...m, [g]: { ...m[g], ...patch } })); };

  const save = async () => {
    setMsg(null);
    setBadGoal(null);
    if (!reason.trim()) { setMsg({ ok: false, text: ERRS.reason_required }); return; }
    if (anyErr) return;
    setBusy(true);
    try {
      await api.post("referral-rules", {
        rules: rows.map((r) => ({ goal_key: r.goal_key, enabled: edits[r.goal_key].enabled, referral_day: Number(edits[r.goal_key].day), cut_off: Number(edits[r.goal_key].cut) })),
        reason: reason.trim(),
      });
      setReason("");
      setMsg({ ok: true, text: "Saved." });
      dirty.current = false;
      await client.invalidateQueries({ queryKey: [key, "referral-rules"] });
    } catch (e) {
      const ae = e instanceof AdminError ? e : null;
      const g = ae && typeof ae.body.goal_key === "string" ? ae.body.goal_key : null;
      if (g) setBadGoal({ goal: g, field: ae!.key === "bad_referral_day" ? "day" : ae!.key === "bad_cut_off" ? "cut" : null, text: ae!.message });
      setMsg({ ok: false, text: ae?.key === "rules_partly_saved" ? partlySaved(ae.body.goal_keys, rows)
        : ae?.key === "reason_required" || ae?.key === "reason_too_long" || ae?.key === "writes_off" ? ae.message : SAVE_FAILED });
      // Some goals did save: reload "Last changed"; the form is dirty, so the edits stay.
      if (ae?.key === "rules_partly_saved") void client.invalidateQueries({ queryKey: [key, "referral-rules"] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHead
        title="Referral rules"
        sub="Below the cut-off at the first check-in on or after the day, the app suggests the nutrition analyst and opens a ticket."
      />
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <LoadError what="load" error={q.error} />
      ) : (
        <Card>
          <CardContent className="space-y-3">
            {rows.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Goal</TableHead><TableHead>On</TableHead><TableHead>Check on day</TableHead>
                    <TableHead>Cut-off (results rating 1–10)</TableHead><TableHead>Last changed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const e = edits[r.goal_key];
                    if (!e) return null;
                    const er = errs[r.goal_key];
                    const server = badGoal?.goal === r.goal_key ? badGoal : null;
                    const dayErr = er.day || (server?.field === "day" ? server.text : undefined);
                    const cutErr = er.cut || (server?.field === "cut" ? server.text : undefined);
                    return (
                      <TableRow key={r.goal_key}>
                        <TableCell className="font-semibold">{r.goal}</TableCell>
                        <TableCell>
                          <Checkbox aria-label={`On for ${r.goal}`} checked={e.enabled} disabled={!writes} onCheckedChange={(c) => set(r.goal_key, { enabled: c === true })} />
                        </TableCell>
                        <TableCell className="align-top">
                          <Input aria-label={`Check on day for ${r.goal}`} className="w-24" type="number" min={1} max={60} step={1} disabled={!writes}
                            value={e.day} aria-invalid={!!dayErr || undefined} onChange={(x) => set(r.goal_key, { day: x.target.value })} />
                          {dayErr ? <p role="alert" className="mt-1 rounded px-2 py-1 text-xs">{dayErr}</p> : null}
                        </TableCell>
                        <TableCell className="align-top">
                          <Input aria-label={`Cut-off for ${r.goal}`} className="w-24" type="number" min={1} max={10} step="any" disabled={!writes}
                            value={e.cut} aria-invalid={!!cutErr || undefined} onChange={(x) => set(r.goal_key, { cut: x.target.value })} />
                          {cutErr ? <p role="alert" className="mt-1 rounded px-2 py-1 text-xs">{cutErr}</p> : null}
                        </TableCell>
                        <TableCell className="text-ks-grey-1">{r.updated_by ? `${r.updated_by}, ${when(r.updated_at)}` : "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Empty>No goals have a referral rule.</Empty>
            )}
            {writes ? (
              <div className="space-y-2">
                <Label htmlFor="rr-reason">Why are you changing this? (required)</Label>
                <Textarea id="rr-reason" rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
                {msg ? (
                  msg.ok ? <p className="text-sm text-ks-pass" role="status">{msg.text}</p> : <Alert><AlertDescription>{msg.text}</AlertDescription></Alert>
                ) : null}
                <Button disabled={busy || anyErr || !rows.length} onClick={save}>Save rules</Button>
              </div>
            ) : (
              <p className="text-sm text-ks-grey-1">Read only: saving is switched off on this system, so the rules above are shown but cannot be changed.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
